import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Menu, X } from 'lucide-react';
import '../styles/Header.css';
import UserIconSimple from '../components/UserIconSimple';
import UserIconName from '../components/UserIconName';
import { useAuth } from '../context/AuthContext';
import { useSearch } from '../context/SearchContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const { user, logout } = useAuth();
  const { searchTerm, setSearchTerm } = useSearch(); 
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);


  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setSearchTerm('');
      return;
    }
    if (location.pathname !== '/') {
      navigate('/');
    }
    // Ocultar búsqueda en mobile después de buscar
    if (isMobile) {
      setIsSearchVisible(false);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim() === '' && location.pathname !== '/') {
      navigate('/');
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Si se abre el menú, cerrar búsqueda
    if (!isMobileMenuOpen) {
      setIsSearchVisible(false);
    }
  };

  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    // Si se abre la búsqueda, cerrar menú
    if (!isSearchVisible) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    setIsSearchVisible(false);
  };

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width <= 768;
      const tablet = width <= 1024 && width > 768;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      
      // Si cambiamos a desktop, cerrar menús móviles
      if (!mobile) {
        setIsMobileMenuOpen(false);
        setIsSearchVisible(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <>
      <header className="header header-transparent">
        <div className="header-content">
          {/* Logo - siempre visible */}
          <div className="logo">
            <img src="/guincho-logo.png" alt="Guincho Logo" className="logo-img" />
            <span className="logo-text">Guincho Garage</span>
          </div>

          {/* Desktop y Tablet: Navegación completa */}
          {!isMobile && (
            <div className="nav-section">
              {/* Enlaces de navegación */}
              <nav className="nav-left">
                <Link to="/">Inicio</Link>
                <Link to="/categorias">Categorías</Link>
                {user && parseInt(user.tipo_usuario) === 0 && (
                  <Link to="/admin">Admin</Link>
                )}
              </nav>

              {/* Barra de búsqueda */}
              <div className="search-container">
                <form onSubmit={handleSearch} className="search-form">
                  <div className="search-input-wrapper">
                    <Search className="search-icon" size={18} />
                    <input
                      type="text"
                      placeholder="Buscar vehículos... (ej: GR 86, Corolla)"
                      value={searchTerm}
                      onChange={handleInputChange}
                      className="search-input"
                    />
                  </div>
                </form>
              </div>

              {/* Usuario / Login */}
              <nav className="nav-right">
                {user ? (
                  <div className="user-dropdown" ref={dropdownRef}>
                    <button
                      onClick={toggleDropdown}
                      className="user-dropdown-trigger"
                    >
                      {isTablet ? <UserIconSimple /> : <UserIconName userName={user.nombre} />}
                      <ChevronDown 
                        size={16} 
                        style={{ 
                          transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}
                      />
                    </button>
                    
                    {isDropdownOpen && (
                      <div className="dropdown-menu">
                        <button onClick={handleLogout} className="dropdown-item">
                          Cerrar Sesión
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link to="/iniciarsesion" className="nav-link">
                    <UserIconSimple />
                  </Link>
                )}
              </nav>
            </div>
          )}

          {/* Mobile: Controles compactos */}
          {isMobile && (
            <div className="mobile-controls">
              <button 
                onClick={toggleSearch}
                className={`mobile-btn ${isSearchVisible ? 'active' : ''}`}
                aria-label="Buscar"
              >
                <Search size={20} />
              </button>

              {user ? (
                <div className="user-dropdown mobile" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    className="mobile-btn"
                    aria-label="Menú usuario"
                  >
                    <UserIconSimple />
                    <ChevronDown 
                      size={12} 
                      style={{ 
                        transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="dropdown-menu mobile">
                      <button onClick={handleLogout} className="dropdown-item">
                        Cerrar Sesión
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/iniciarsesion" className="mobile-btn">
                  <UserIconSimple />
                </Link>
              )}

              <button 
                onClick={toggleMobileMenu}
                className={`mobile-btn ${isMobileMenuOpen ? 'active' : ''}`}
                aria-label="Menú"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          )}
        </div>

        {/* Mobile: Barra de búsqueda expandible */}
        {isMobile && isSearchVisible && (
          <div className="mobile-search">
            <div className="mobile-search-content">
              <form onSubmit={handleSearch} className="search-form mobile">
                <div className="search-input-wrapper">
                  <Search className="search-icon" size={18} />
                  <input
                    type="text"
                    placeholder="Buscar vehículos..."
                    value={searchTerm}
                    onChange={handleInputChange}
                    className="search-input"
                    autoFocus
                  />
                  <button 
                    type="button" 
                    onClick={toggleSearch}
                    className="search-close"
                  >
                    <X size={18} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </header>

      {/* Mobile: Menú de navegación */}
      {isMobile && isMobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-content">
            <Link to="/" onClick={handleNavClick}>Inicio</Link>
            <Link to="/categorias" onClick={handleNavClick}>Categorías</Link>
            {user && parseInt(user.tipo_usuario) === 0 && (
              <Link to="/admin" onClick={handleNavClick}>Admin</Link>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
